import { useEffect } from 'react';
import { useUserVFS } from '../../hooks/useUserVFS';
import { ensureDir, ensureFile } from '../../utils/filesystem';

const MODULE_ROOT = 'C:\\Program Files\\Modules\\Grand Theft Boxy';
const MODULE_MANIFEST_PATH = `${MODULE_ROOT}\\manifest.json`;
const MODULE_INDEX_PATH = `${MODULE_ROOT}\\index.html`;
const GAME_URL = '/os/games/grand-theft-boxy/index.html';
const LOCAL_SCRIPT_URL = '/os/games/grand-theft-boxy/build/sketchbook.min.js';
const LOCAL_WORLD_URL = '/os/games/grand-theft-boxy/build/assets/world.glb';
const CDN_SCRIPT_URL = 'https://cdn.jsdelivr.net/gh/swift502/Sketchbook@master/build/sketchbook.min.js';
const CDN_WORLD_URL = 'https://cdn.jsdelivr.net/gh/swift502/Sketchbook@master/build/assets/world.glb';

const moduleManifest = JSON.stringify(
  {
    name: 'Grand Theft Boxy',
    version: '0.1.0',
    description: 'Boxy driving sandbox hosted as a MapleOS web game.',
    categories: ['games'],
    type: 'web',
    entry: { html: 'index.html' },
  },
  null,
  2
);

const moduleHtml = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Grand Theft Boxy</title>
    <style>
      html,
      body {
        margin: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: #060817;
        color: #f8fafc;
        font-family: Arial, sans-serif;
      }

      body > canvas {
        display: block;
      }

      .asset-error {
        position: fixed;
        inset: 0;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: rgba(6, 8, 23, 0.92);
        text-align: center;
      }

      .asset-error__card {
        max-width: 640px;
        border: 1px solid rgba(148, 163, 184, 0.35);
        background: rgba(15, 23, 42, 0.88);
        border-radius: 16px;
        padding: 20px 24px;
        box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35);
      }

      .asset-error__title {
        margin: 0 0 12px;
        font-size: 24px;
      }

      .asset-error__copy {
        margin: 0;
        line-height: 1.5;
        color: #cbd5e1;
      }

      .asset-error__paths {
        margin: 16px 0 0;
        color: #e2e8f0;
        font-size: 14px;
        line-height: 1.5;
      }
    </style>
  </head>
  <body>
    <div id="asset-error" class="asset-error">
      <div class="asset-error__card">
        <h1 class="asset-error__title">Grand Theft Boxy assets are missing</h1>
        <p class="asset-error__copy">
          Sketchbook expects its original build folder layout, not a flat assets folder.
        </p>
        <div class="asset-error__paths">
          <div><strong>Required:</strong> <code>${LOCAL_SCRIPT_URL}</code></div>
          <div><strong>Required:</strong> <code>${LOCAL_WORLD_URL}</code></div>
          <div><strong>Also used:</strong> <code>boxman.glb</code>, <code>car.glb</code>, <code>heli.glb</code>, <code>airplane.glb</code> in <code>/os/games/grand-theft-boxy/build/assets/</code></div>
        </div>
      </div>
    </div>

    <script>
      const LOCAL_SCRIPT_URL = '${LOCAL_SCRIPT_URL}';
      const LOCAL_WORLD_URL = '${LOCAL_WORLD_URL}';
      const CDN_SCRIPT_URL = '${CDN_SCRIPT_URL}';
      const CDN_WORLD_URL = '${CDN_WORLD_URL}';
      const assetError = document.getElementById('asset-error');

      function showAssetError() {
        if (assetError) {
          assetError.style.display = 'flex';
        }
      }

      function cleanupExistingWorld() {
        if (window.__grandTheftBoxyWorld && typeof window.__grandTheftBoxyWorld === 'object') {
          try {
            if (window.__grandTheftBoxyWorld.renderer && window.__grandTheftBoxyWorld.renderer.domElement) {
              window.__grandTheftBoxyWorld.renderer.domElement.remove();
            }
          } catch (error) {
            console.warn('Failed to clean up existing Sketchbook world', error);
          }
        }
      }

      function loadScript(src) {
        return new Promise((resolve, reject) => {
          const existingScript = document.querySelector('script[data-grand-theft-boxy="' + src + '"]');
          if (existingScript) {
            if (window.Sketchbook) {
              resolve();
              return;
            }

            existingScript.addEventListener('load', () => resolve(), { once: true });
            existingScript.addEventListener('error', () => reject(new Error('Failed to load ' + src)), {
              once: true,
            });
            return;
          }

          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.dataset.grandTheftBoxy = src;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load ' + src));
          document.body.appendChild(script);
        });
      }

      async function hasLocalAssets() {
        try {
          const [scriptResponse, worldResponse] = await Promise.all([
            fetch(LOCAL_SCRIPT_URL, { method: 'HEAD' }),
            fetch(LOCAL_WORLD_URL, { method: 'HEAD' }),
          ]);

          return scriptResponse.ok && worldResponse.ok;
        } catch {
          return false;
        }
      }

      function startWorld(worldUrl) {
        cleanupExistingWorld();
        window.__grandTheftBoxyWorld = new window.Sketchbook.World(worldUrl);
      }

      (async () => {
        const useLocalAssets = await hasLocalAssets();
        const scriptUrl = useLocalAssets ? LOCAL_SCRIPT_URL : CDN_SCRIPT_URL;
        const worldUrl = useLocalAssets ? LOCAL_WORLD_URL : CDN_WORLD_URL;

        try {
          await loadScript(scriptUrl);
          startWorld(worldUrl);
        } catch (primaryError) {
          if (useLocalAssets) {
            try {
              await loadScript(CDN_SCRIPT_URL);
              startWorld(CDN_WORLD_URL);
              return;
            } catch (fallbackError) {
              console.error('Failed to load Grand Theft Boxy assets', fallbackError);
            }
          } else {
            console.error('Failed to load Grand Theft Boxy assets', primaryError);
          }

          showAssetError();
        }
      })();
    </script>
  </body>
</html>`;

export default function GrandTheftBoxy() {
  const { isLoaded } = useUserVFS();

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    let cancelled = false;

    const installModule = async () => {
      await ensureDir(MODULE_ROOT);

      if (cancelled) {
        return;
      }

      await ensureFile(MODULE_MANIFEST_PATH, moduleManifest);
      await ensureFile(MODULE_INDEX_PATH, moduleHtml);

      if (!cancelled) {
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: MODULE_ROOT } }));
      }
    };

    void installModule();

    return () => {
      cancelled = true;
    };
  }, [isLoaded]);

  return (
    <div className="h-full w-full bg-black">
      <iframe
        src={GAME_URL}
        title="Grand Theft Boxy"
        className="h-full w-full border-0"
        allow="autoplay; fullscreen; gamepad; pointer-lock"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}